import { useEffect, useMemo, useState } from "react";
import { ArrowRightLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const TYPE_LABELS = { attendance: "Attendance", evaluation_past_due: "Evaluation past due", evaluation_follow_up: "Evaluation follow-up", supervision_follow_up: "Supervision log follow-up", recurring_reports: "Recurring reports", other: "Manual and other tasks" };
const TYPE_ORDER = Object.keys(TYPE_LABELS);

export const ReassignTasksPanel = ({ member, onClose, onDone }) => {
  const [owners, setOwners] = useState([]);
  const [openTasks, setOpenTasks] = useState(null);
  const [target, setTarget] = useState("");
  const [note, setNote] = useState("");
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.get("/tasks/owners"), api.get("/tasks", { params: { owner_id: member.id } })])
      .then(([own, tasks]) => {
        setOwners(own.data.filter((o) => o.id !== member.id));
        setOpenTasks(tasks.data);
        setChosen(new Set(tasks.data.map((task) => task.task_type || "other")));
      })
      .catch((e) => setError(formatApiError(e)));
  }, [member.id]);

  const counts = useMemo(() => (openTasks || []).reduce((acc, task) => ({ ...acc, [task.task_type || "other"]: (acc[task.task_type || "other"] || 0) + 1 }), {}), [openTasks]);
  const selected = useMemo(() => (openTasks || []).filter((task) => chosen?.has(task.task_type || "other")), [openTasks, chosen]);
  const allTypes = TYPE_ORDER.filter((key) => counts[key]);
  const everything = chosen && allTypes.every((key) => chosen.has(key));

  const toggle = (key) => setChosen((current) => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; });

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await api.post("/tasks/reassign", { from_owner_id: member.id, to_owner_id: target, note, task_types: everything ? [] : [...chosen] });
      onDone(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  const name = `${member.first_name} ${member.last_name}`.trim();
  return (
    <form className="inline-edit-panel team-member-panel" data-testid="reassign-tasks-panel" onSubmit={submit}>
      <div className="section-heading">
        <div>
          <h2><ArrowRightLeft size={16} /> Reassign open tasks</h2>
          <p>Choose which task types move from {name}. Open, in-progress, blocked and awaiting-verification tasks of those types go to the new owner; the handoff is logged on each task and the new owner (with the CEO copied) receives one digest.</p>
        </div>
      </div>
      {error && <p className="form-message error-message" data-testid="reassign-tasks-error">{error}</p>}
      <p className="pairing-meta" data-testid="reassign-tasks-count">
        {openTasks === null ? "Counting open tasks…" : `${openTasks.length} open task${openTasks.length === 1 ? "" : "s"} currently owned by ${name}.`}
      </p>
      {allTypes.length > 0 && (
        <fieldset className="reassign-type-grid" data-testid="reassign-tasks-types">
          <legend className="field-label">Task types to move</legend>
          {allTypes.map((key) => (
            <label key={key} className={`reassign-type ${chosen?.has(key) ? "checked" : ""}`}>
              <input data-testid={`reassign-type-${key}`} type="checkbox" checked={chosen?.has(key) || false} onChange={() => toggle(key)} />
              <span>{TYPE_LABELS[key]}</span>
              <small>{counts[key]}</small>
            </label>
          ))}
        </fieldset>
      )}
      {selected.length > 0 && (
        <ul className="reassign-task-list" data-testid="reassign-tasks-list">
          {selected.slice(0, 8).map((task) => <li key={task.id}>{task.title} <small>due {task.due_date} · {task.status}</small></li>)}
          {selected.length > 8 && <li><small>…and {selected.length - 8} more</small></li>}
        </ul>
      )}
      <div className="task-form-grid">
        <label>New owner
          <select data-testid="reassign-tasks-owner" required value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Select the new owner</option>
            {owners.map((o) => <option key={o.id} value={o.id}>{`${o.name} · ${o.role === "hr" ? "HR" : o.role === "operations" ? "Operations" : "System Administrator"}`}</option>)}
          </select>
        </label>
        <label className="task-form-wide">Handoff note (optional)<textarea data-testid="reassign-tasks-note" rows={2} placeholder="e.g. Covering during offboarding" value={note} onChange={(e) => setNote(e.target.value)} /></label>
      </div>
      <div className="team-confirm-actions">
        <button className="button button-secondary" data-testid="reassign-tasks-cancel" type="button" onClick={onClose}>Cancel</button>
        <button className="button button-primary" data-testid="reassign-tasks-confirm" disabled={busy || !target || !selected.length} type="submit">
          <ArrowRightLeft size={14} /> Move {selected.length} task{selected.length === 1 ? "" : "s"}
        </button>
      </div>
    </form>
  );
};
